// ABOUTME: Removes a deleted parameter-history row from a cached history query.
// ABOUTME: Lets deleteItem update the list from the mutation's success alone, without depending on a refetch.

import type { ApolloCache } from "@apollo/client"
import type { DocumentNode } from "graphql"
import type { TypedDocumentNode } from "@graphql-typed-document-node/core"

type HistoryQueryData = Record<string, unknown>

interface EvictHistoryRowArgs {
  /** The history query whose cached result holds the row. */
  document: DocumentNode | TypedDocumentNode<unknown>
  /** The exact variables the query was issued with. */
  variables: Record<string, unknown>
  /** Field name of the row array in the query result. */
  responseKey: string
  /** `date` of the row to drop (the delete key the server matched at second precision). */
  date: string
}

/**
 * Drops the row with the given `date` from a cached history query so the list
 * reflects the delete immediately. A subsequent refetch is then best-effort:
 * its failure can no longer resurrect the just-deleted row or surface a false
 * error. Rows are keyed by their `date` (the history table's primary key is
 * `(profile_id, date, name)`), so an exact-string match removes exactly one row.
 */
export const evictHistoryRow = (
  cache: ApolloCache,
  { document, variables, responseKey, date }: EvictHistoryRowArgs,
): void => {
  cache.updateQuery<HistoryQueryData>({ query: document, variables }, (existing) => {
    if (!existing) return existing
    const rows = existing[responseKey]
    if (!Array.isArray(rows)) return existing
    return {
      ...existing,
      [responseKey]: rows.filter((row) => (row as { date?: string }).date !== date),
    }
  })
}
